import * as React from 'react'
import { useState, useRef, useEffect, useContext, useCallback, useMemo } from 'react'
import { useQuery, gql } from '@apollo/client'
import gsap from 'gsap'
import { SplitText } from 'gsap/SplitText'
import { useGSAP } from '@gsap/react'
import DeviceContext from '../context/DeviceContext'
import UserContext from '../context/UserContext.js'
import styles from './ManyTranslationsFullView.module.scss'
import { speak } from '../speech/speak.js' // Import speak function
import voices from '../speech/voices' // Import voices mapping
import flags from '../flags.js'
import languages, { getLanguageAcronym } from '../languages.js'
gsap.registerPlugin(SplitText)
// GraphQL Query
const GET_ALL_TRANSLATION_USER_SENTENCES = gql`
  query AllTranslationUserSentences($username: String!, $sourceLang: String) {
    allTranslationUserSentences(username: $username, sourceLang: $sourceLang) {
      sentence {
        id
        text
        lang
        user
        dateAdded
        dateLastModified
      }
      translations {
        id
        text
        lang
        user
        dateAdded
        dateLastModified
      }
    }
  }
`
export default function ManyTranslationsFullView({
  isManyTranslationsFullViewVisible,
  setIsManyTranslationsFullViewVisible,
  initialSourceLang = 'eng',
  initialTargetLang = 'Portuguese',
  voiceSpeed = 1.0 // Default voice speed
}) {
  const { username } = useContext(UserContext)
  const { isMobile } = useContext(DeviceContext)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [sourceLang, setSourceLang] = useState(initialSourceLang)
  const [targetLanguage, setTargetLanguage] = useState(initialTargetLang)
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false)
  const sourceSentenceRef = useRef(null)
  const translationsRef = useRef(null)
  const mainRef = useRef(null) // Ref for the main container
  const swipeStartX = useRef(0)
  const swipeEndX = useRef(0)
  const swipeStartY = useRef(0)
  const swipeEndY = useRef(0)
  // Fetch data using Apollo Client
  const { loading, error, data } = useQuery(GET_ALL_TRANSLATION_USER_SENTENCES, {
    variables: { username, sourceLang },
    skip: !username || !isManyTranslationsFullViewVisible
  })
  const sentences = data?.allTranslationUserSentences || []
  // Use all languages from languages.js for the menu
  const availableLanguages = useMemo(() => Object.keys(languages).sort(), [])
  // Handle click to speak for source sentence
  const handleClickSource = useCallback(() => {
    const currentSentence = sentences[currentIndex]?.sentence
    if (currentSentence) {
      speak(currentSentence.text, voices[currentSentence.lang], voiceSpeed)
    }
  }, [sentences, currentIndex, voiceSpeed])
  // Handle click to speak for a translation
  const handleClickTranslation = useCallback(
    (translation) => {
      speak(translation.text, voices[translation.lang], voiceSpeed)
    },
    [voiceSpeed]
  )
  // Swipe handling functions
  const handleTouchStart = (e) => {
    swipeStartX.current = e.touches[0].clientX
    swipeStartY.current = e.touches[0].clientY // Track vertical start
  }
  const handleTouchMove = (e) => {
    swipeEndX.current = e.touches[0].clientX
    swipeEndY.current = e.touches[0].clientY // Track vertical move
  }
  const handleTouchEnd = () => {
    const swipeDistanceX = swipeEndX.current - swipeStartX.current
    const swipeDistanceY = swipeEndY.current - swipeStartY.current // Calculate vertical swipe distance
    // Handle horizontal swipes
    if (swipeDistanceX > 150) {
      // Swipe right
      setCurrentIndex((prevIndex) => (prevIndex - 1 < 0 ? sentences.length - 1 : prevIndex - 1))
    } else if (swipeDistanceX < -150) {
      // Swipe left
      setCurrentIndex((prevIndex) => (prevIndex + 1 === sentences.length ? 0 : prevIndex + 1))
    }
  }
  // Ensure focus when component is visible
  useEffect(() => {
    if (isManyTranslationsFullViewVisible && mainRef.current && !isMobile) {
      mainRef.current.focus()
    }
  }, [isManyTranslationsFullViewVisible, isMobile])
  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      console.log('Keydown detected:', event.key, { isMobile, isManyTranslationsFullViewVisible, sentencesLength: sentences.length })
      if (!isMobile && isManyTranslationsFullViewVisible && sentences.length > 0) {
        if (event.key === 'ArrowRight') {
          setCurrentIndex((prev) => (prev + 1) % sentences.length)
        } else if (event.key === 'ArrowLeft') {
          setCurrentIndex((prev) => (prev - 1 + sentences.length) % sentences.length)
        } else if (event.key === 'e') {
          setIsManyTranslationsFullViewVisible(false)
        } else if (event.key === 'r' || event.key === 'R') {
          setCurrentIndex(Math.floor(Math.random() * sentences.length))
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [sentences.length, isMobile, isManyTranslationsFullViewVisible, setIsManyTranslationsFullViewVisible])
  // Handle loading and error states
  if (loading) return <div className={styles.loading}>Loading...</div>
  if (error) return <div className={styles.error}>Error: {error.message}</div>
  if (sentences.length === 0) return <div className={styles.noData}>No sentences found</div>
  // Current sentence and translations
  const currentSentence = sentences[currentIndex]?.sentence
  const translations = sentences[currentIndex]?.translations || []
  // Convert targetLanguage name to acronym for filtering and font class
  const targetLangAcronym = targetLanguage === 'All' ? null : getLanguageAcronym(targetLanguage)
  const displayedTranslations = targetLanguage === 'All' ? translations : translations.filter((translation) => translation.lang === targetLangAcronym)
  // Determine font class based on targetLanguage
  const fontClass = targetLanguage === 'All' ? '' : `font${targetLangAcronym.charAt(0).toUpperCase() + targetLangAcronym.slice(1)}`
  return (
    <>
      <div
        className={styles.manyTranslationsFullView}
        ref={mainRef}
        tabIndex={-1} // Make the div focusable
        onTouchStart={isMobile ? handleTouchStart : null}
        onTouchMove={isMobile ? handleTouchMove : null}
        onTouchEnd={isMobile ? handleTouchEnd : null}>
        <div className={styles.container}>
          <div className={styles.closeButton} onClick={() => setIsManyTranslationsFullViewVisible(false)}>
            X
          </div>
          <div className={styles.sourceSentence}>
            <span
              ref={sourceSentenceRef}
              className={`${styles.textNormal} ${styles.clickable}`}
              onClick={isMobile ? undefined : handleClickSource}
              onPointerUp={isMobile ? handleClickSource : undefined}>
              {currentSentence?.text || 'No sentence selected'}
            </span>
          </div>
          <div className={`${styles.translations} ${styles[fontClass]}`} ref={translationsRef}>
            {displayedTranslations.length > 0 ? (
              displayedTranslations.map((translation) => (
                <div key={translation.id} className={styles.translation}>
                  <span
                    className={`${styles.textNormal} ${styles.clickable}`}
                    onClick={isMobile ? undefined : () => handleClickTranslation(translation)}
                    onPointerUp={isMobile ? () => handleClickTranslation(translation) : undefined}>
                    <span className={styles.lang}>{translation.lang}</span>: {translation.text}
                  </span>
                </div>
              ))
            ) : (
              <div className={styles.noTranslation}>No translation available for {targetLanguage}</div>
            )}
          </div>
          <div className={styles.controlsContainer}>
            <div className={styles.currentTargetLanguage} onClick={() => setIsLanguageMenuOpen(true)}>
              {targetLanguage === 'All' ? '🌍' : flags[getLanguageAcronym(targetLanguage)]}
            </div>
          </div>
        </div>
      </div>
      {isLanguageMenuOpen && (
        <div className={styles.languageMenu} onClick={() => setIsLanguageMenuOpen(false)}>
          <div className={styles.flagGrid} onClick={(e) => e.stopPropagation()}>
            {['All', ...availableLanguages].map((lang) => (
              <div
                key={lang}
                className={styles.flagItem}
                onClick={() => {
                  setTargetLanguage(lang === 'All' ? 'All' : languages[lang])
                  setIsLanguageMenuOpen(false)
                }}>
                {lang === 'All' ? '🌍' : flags[lang]}
                <div className={styles.languageName}>{lang === 'All' ? 'All' : languages[lang]}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}
